import {
  Modal,
  Button,
  Group,
  Select,
  Radio,
  Flex,
  Box,
  rem,
  Tooltip,
  Text,
  Loader,
  Skeleton,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import useStyles from "./style.js";
import { useSelector } from "react-redux";
import { useState, useEffect, useRef } from "react";
import { DropDownArrowSvg } from "../Svgs/DropDownArrowSvg.jsx";
import { calculateDistanceMiles } from "../../services/UserTracking.js";
import { getDeviceLocation } from "../../utils/geolocation.js";
import { getAllFacilitiesModal } from "../../services/facility.js";
import { searchAddress, getAddressByPlaceId } from "../../services/dashboard";
// Build full address string from facility object for Google API
const buildFullAddress = (facility) => {
  if (!facility) return null;
  const city = facility.city || facility.addressLineTwo || facility.address2;
  const parts = [
    facility.address,
    city,
    facility.state,
    facility.zipcode,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : null;
};

// Build address string from client's form1 currentAddress fields
const buildClientAddress = (client) => {
  if (!client?.form1) return null;
  const { currentAddress, currentCity, currentState, currentZipCode } = client.form1;
  const parts = [currentAddress, currentCity, currentState, currentZipCode].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : null;
};

// Build grouped Select options from client's registered address + otherLocations
const getClientAddressOptions = (client) => {
  if (!client) return [];
  const options = [];
  const currentAddr = buildClientAddress(client);
  if (currentAddr) {
    options.push({
      value: "current",
      label: currentAddr,
      group: "Current Address",
      fullAddress: currentAddr,
      obj: {
        label: "Current Address",
        address: client.form1?.currentAddress || "",
        city: client.form1?.currentCity || "",
        state: client.form1?.currentState || "",
        zipCode: client.form1?.currentZipCode || "",
      },
    });
  }
  (client.form1?.otherLocations || []).forEach((loc, i) => {
    const addr = [loc.address, loc.city, loc.state, loc.zipCode]
      .filter(Boolean)
      .join(", ");
    if (addr) {
      options.push({
        value: `other_${i}`,
        label: `${loc.label} – ${loc.address}`,
        group: "Other Locations",
        fullAddress: addr,
        obj: loc,
      });
    }
  });
  return options;
};

function SelectCheckInType({
  opened,
  close,
  open,
  handleModal,
  selectedFacility,
  setSelectedFacility,
  selectedClient,
  setSelectedClient,
  setDisableClockBtn,
  selectedService,
  setSelectedService,
  setEvvLocationData,
}) {
  const { classes } = useStyles();
  // const navigate = useNavigate();
  const { profile } = useSelector((state) => state?.profile);
  const { allPatients } = useSelector((state) => state?.patient);
  const { allFacilities } = useSelector((state) => state?.facility);
  const [selectedType, setSelectedType] = useState();
  const [facilityValue, setFacilityValue] = useState(null);
  const [warningText, setWarningText] = useState(null);
  // const [origins, setOrigins] = useState(null);
  // const [destinations, setDestinations] = useState(null);
  const [miles, setMiles] = useState(null);
  const [milesError, setMilesError] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const isStaff = profile?.user?.type?.toLowerCase() === "staff";
  const [locationPermission, setLocationPermission] = useState("prompt");
  const [overrideAddress, setOverrideAddress] = useState("");
  const [selectedPickupLocation, setSelectedPickupLocation] = useState(null);
  const [selectedPickupObj, setSelectedPickupObj] = useState(null);
  const [isClockLoading, setIsClockLoading] = useState(false);
  const [isLocationLoading, setIsLocationLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showOneTimeEdit, setShowOneTimeEdit] = useState(false);
  const [isUsingOneTimeAddress, setIsUsingOneTimeAddress] = useState(false);
  const [oneTimeForm, setOneTimeForm] = useState({ address: "", city: "", state: "", zipCode: "" });
  const [oneTimeSuggestions, setOneTimeSuggestions] = useState([]);
  const [showOneTimeSuggestions, setShowOneTimeSuggestions] = useState(false);
  const [oneTimeAddrLoading, setOneTimeAddrLoading] = useState(false);
  const oneTimeDebounceRef = useRef(null);

  // const facilities =
  //   profile?.user?.facilityId?.filter(
  //     (_f) =>
  //       (_f?.checkInType === selectedType ||
  //         _f?.checkInType === "no-clockin") &&
  //       _f?.deleted === false
  //   ) || [];

  let facilities = [];

  if (isStaff) {
    facilities =
      profile?.user?.facilityId?.filter(
        (_f) =>
          _f?.checkInType === selectedType &&
          _f?.checkInType !== "no-clockin" &&
          _f?.deleted === false,
      ) || [];
  } else {
    facilities =
      allFacilities?.facilities?.filter(
        (_f) =>
          _f?.checkInType === selectedType &&
          _f?.checkInType !== "no-clockin" &&
          _f?.deleted === false,
      ) || [];
  }

  // Find the EVV facility for the selected client by matching client's facilityId
  const getClientEvvFacility = () => {
    if (!selectedClient) return null;
    const client = allPatients?.find((_p) => _p?.id === selectedClient);
    const clientFacilityIds = (client?.facilityId || []).map((f) =>
      typeof f === "string" ? f : f?.id || f?._id,
    );
    // Find a facility the client belongs to that has EVV enabled
    return (
      facilities?.find(
        (f) =>
          clientFacilityIds.includes(f?.id || f?._id) &&
          f?.evvRequired === "yes",
      ) || null
    );
  };

  const clientEvvFacility =
    selectedType === "client" ? getClientEvvFacility() : null;

  // Compute EVV max distance for the current selection (facility or client)
  const currentMaxDistance = (() => {
    if (selectedType === "facility" && selectedFacility) {
      return selectedFacility?.evvMaxDistance || 0.25;
    }
    if (selectedType === "client" && clientEvvFacility) {
      return clientEvvFacility.evvMaxDistance || 0.25;
    }
    return 0.25;
  })();

  const clockInHandler = () => {
    setIsSubmitting(true);
    handleModal("clock in");
  };

  const onChangeFacility = (value) => {
    setSelectedClient(null);
    // setAddressNotFound(false);
    setMiles(null);
    setMilesError(false);
    setFacilityValue(value);
    const facilities = isStaff
      ? profile?.user?.facilityId
      : allFacilities?.facilities;
    let facility_index = facilities?.findIndex((_f) => _f?.id === value);
    // set facility object for payload
    setSelectedFacility(facilities[facility_index]);
    // Call immediately — only when GPS is on
    if (userLocation?.source === "gps") calculateDistance({ facilityObj: facilities[facility_index] });

    if (!facilities[facility_index]?.checkInType) {
      setWarningText("Ask admin to assign chock in type to the facility");
    } else {
      setWarningText(null);
    }
  };

  // Fires when GPS location resolves after a selection was already made,
  // or when overrideAddress changes. Only runs when GPS is available.
  useEffect(() => {
    if (userLocation?.source !== "gps") return;
    if (selectedType === "client") {
      if (selectedClient) calculateDistance({ clientId: selectedClient });
    } else {
      if (selectedFacility) calculateDistance({ facilityObj: selectedFacility });
    }
  }, [userLocation, overrideAddress]);

  const onChangeClient = (value) => {
    setSelectedClient(value);
    setOverrideAddress("");
    setSelectedPickupLocation("current");
    setSelectedPickupObj(null);
    setMiles(null);
    setMilesError(false);
    setShowOneTimeEdit(false);
    setIsUsingOneTimeAddress(false);
    setOneTimeForm({ address: "", city: "", state: "", zipCode: "" });
    setOneTimeSuggestions([]);
    const client = allPatients?.find((_p) => _p?.id === value);
    const addr = buildClientAddress(client);
    setSelectedAddress(addr || null);
    if (userLocation?.source === "gps" && addr) {
      calculateDistance({ clientId: value, overrideAddressParam: "" });
    }
  };

  const handlePickupLocationChange = (value) => {
    const client = allPatients?.find((_p) => _p?.id === selectedClient);
    const options = getClientAddressOptions(client);
    const selected = options.find((o) => o.value === value);
    setSelectedPickupLocation(value);
    setSelectedPickupObj(selected?.obj || null);
    // "current" = use client's registered address (empty overrideAddress falls through to buildClientAddress)
    setOverrideAddress(value === "current" || !value ? "" : selected?.fullAddress || "");
  };

  const onChangeService = (value) => {
    setSelectedService(value);
  };

  const radioChangeHandler = (value) => {
    setFacilityValue(null);
    setSelectedType(value);
    // setSelectedType({f_type: null, f_id: null});
    // setSelectedType({f_type: value, f_id: null});
  };

  let types = [];
  if (isStaff) {
    types = profile?.user?.facilityId
      ?.filter((f) => f?.deleted === false)
      ?.map((f) => f?.checkInType);
  } else {
    types = allFacilities?.facilities
      ?.filter((f) => f?.deleted === false)
      ?.map((f) => f?.checkInType);
  }

  const checkInTypes = new Set(types);

  // const checkInTypes = new Set(
  //   profile?.user?.facilityId
  //   ?.filter((f) => f?.deleted === false)
  //   ?.map((f) => f?.checkInType)
  // );

  const hasFacility = checkInTypes.has("facility");
  const hasClient = checkInTypes.has("client");

  const boxWidth = hasFacility && hasClient ? "48%" : "100%";

  useEffect(() => {
    if (profile?.user?.facilityId) {
      setDisableClockBtn(false);
      for (var a = 0; a < profile?.user?.facilityId?.length; a++) {
        let i = profile?.user?.facilityId[a];
        if (i?.checkInType !== "no-clockin") {
          setDisableClockBtn(false);
        }
      }
    }
  }, []);

  const icon = (
    <DropDownArrowSvg
      color={"#94A3B8"}
      style={{ width: rem(1), height: rem(1) }}
    />
  );

  const handleClose = () => {
    setSelectedFacility(null);
    setSelectedClient(null);
    setSelectedType(null);
    setFacilityValue(null);
    setWarningText(null);
    close();
  };

  // Reset state and fetch fresh location each time the modal opens
  useEffect(() => {
    if (opened) {
      setSelectedType(null);
      setFacilityValue(null);
      setSelectedFacility(null);
      setSelectedClient(null);
      setWarningText(null);
      setMiles(null);
      setMilesError(false);
      setSelectedAddress(null);
      setOverrideAddress("");
      setSelectedPickupLocation(null);
      setSelectedPickupObj(null);
      setEvvLocationData?.(null);
      setIsSubmitting(false);
      setShowOneTimeEdit(false);
      setIsUsingOneTimeAddress(false);
      setOneTimeForm({ address: "", city: "", state: "", zipCode: "" });
      setOneTimeSuggestions([]);
      // Don't clear userLocation here — keep the previous value so the
      // "Please enable browser location" banner doesn't flash on re-open
      // when the browser permission is still granted. Location is refreshed
      // below and updated once the new fix arrives.
      setIsLocationLoading(true);
      getDeviceLocation().then((loc) => {
        if (loc) {
          setUserLocation(loc);
          setLocationPermission("granted");
        } else {
          // Only mark denied (and clear location) if we have no previous
          // location to fall back on — avoids false "enable location" on
          // the second clock-in when browser permission is still active.
          setUserLocation((prev) => {
            if (!prev) setLocationPermission("denied");
            return prev;
          });
        }
        setIsLocationLoading(false);
      });
    }
  }, [opened]);

  useEffect(() => {
    if (selectedType) {
      setMiles(null);
      setSelectedAddress(null);
      setSelectedFacility(null);
      setSelectedClient(null);
      setFacilityValue(null);
      setOverrideAddress("");
      setSelectedPickupLocation(null);
      setSelectedPickupObj(null);
      setShowOneTimeEdit(false);
      setIsUsingOneTimeAddress(false);
      setOneTimeForm({ address: "", city: "", state: "", zipCode: "" });
      setOneTimeSuggestions([]);
    }
  }, [selectedType]);

  const handleOneTimeAddressSearch = (value) => {
    setOneTimeForm((prev) => ({ ...prev, address: value }));
    if (oneTimeDebounceRef.current) clearTimeout(oneTimeDebounceRef.current);
    if (!value || value.length < 3) {
      setOneTimeSuggestions([]);
      setShowOneTimeSuggestions(false);
      return;
    }
    oneTimeDebounceRef.current = setTimeout(async () => {
      setOneTimeAddrLoading(true);
      try {
        const results = await searchAddress(value);
        setOneTimeSuggestions(results || []);
        setShowOneTimeSuggestions(true);
      } catch {
        setOneTimeSuggestions([]);
      }
      setOneTimeAddrLoading(false);
    }, 400);
  };

  const handleOneTimeSelectAddress = async (suggestion) => {
    setShowOneTimeSuggestions(false);
    setOneTimeAddrLoading(true);
    try {
      const placeDetails = await getAddressByPlaceId(suggestion.place_id);
      const components = placeDetails?.result?.address_components || [];
      let streetNumber = "", route = "", city = "", addrState = "", zipcode = "";
      for (const comp of components) {
        if (comp.types.includes("street_number")) streetNumber = comp.long_name;
        if (comp.types.includes("route")) route = comp.long_name;
        if (comp.types.includes("locality")) city = comp.long_name;
        if (comp.types.includes("administrative_area_level_1")) addrState = comp.long_name;
        if (comp.types.includes("postal_code")) zipcode = comp.long_name;
      }
      setOneTimeForm({
        address: [streetNumber, route].filter(Boolean).join(" ") || suggestion.formatted_address,
        city: city || "",
        state: addrState || "",
        zipCode: zipcode || "",
      });
    } catch {
      setOneTimeForm((prev) => ({ ...prev, address: suggestion.formatted_address }));
    }
    setOneTimeAddrLoading(false);
  };

  const handleUseOneTimeAddress = () => {
    const { address, city, state, zipCode } = oneTimeForm;
    const composed = [address, city, state, zipCode].filter(Boolean).join(", ");
    if (!composed) return;
    setIsUsingOneTimeAddress(true);
    setShowOneTimeEdit(false);
    setOverrideAddress(composed);
    setSelectedPickupObj(null);
    if (userLocation?.source === "gps") {
      calculateDistance({ clientId: selectedClient, overrideAddressParam: composed });
    }
  };

  const handleRevertToSavedAddress = () => {
    setIsUsingOneTimeAddress(false);
    setShowOneTimeEdit(false);
    setOneTimeForm({ address: "", city: "", state: "", zipCode: "" });
    setOneTimeSuggestions([]);
    setOverrideAddress("");
    setSelectedPickupLocation("current");
    setSelectedPickupObj(null);
    if (userLocation?.source === "gps") {
      calculateDistance({ clientId: selectedClient, overrideAddressParam: "" });
    }
  };

  const calculateDistance = async ({ clientId = selectedClient, facilityObj = selectedFacility, overrideAddressParam = overrideAddress } = {}) => {
    if (!userLocation) return;
    try {
      setIsClockLoading(true);
      const origins = `${userLocation.lat},${userLocation.lng}`;

      // const result = await axios.get(
      //   `https://maps.googleapis.com/maps/api/distancematrix/json`,
      //   {
      //     params: {
      //       origins: 'New%20York%20City%2C%20NY',
      //       destinations: 'Washington%2C%20DC',
      //       key: API_KEY,
      //       units: 'imperial' // or 'imperial'
      //     }
      //   }
      // );
      let destination = buildFullAddress(facilityObj);
      // Use custom clock-in location address if configured
      if (selectedType === "facility" && facilityObj?.clockInLocation?.type === "custom" && facilityObj.clockInLocation.customAddress?.address) {
        const ca = facilityObj.clockInLocation.customAddress;
        destination = [ca.address, ca.city, ca.state, ca.zipcode].filter(Boolean).join(", ");
      }
      if (selectedType === "client") {
        const client = allPatients?.find((_p) => _p?.id === clientId);
        destination = overrideAddressParam || buildClientAddress(client) || null;
      }

      if (!destination) {
        setSelectedAddress(null);
        setMiles(null);
        setMilesError(false);
        setIsClockLoading(false);
        return;
      }

      let payload = {
        origin: origins,
        destination,
        // units: 'imperial' // or 'imperial'
        mode: "WALKING", // or 'imperial'
      };
      let result = await calculateDistanceMiles(payload);
      if (result?.tracking) {
        let legItem = result?.tracking?.legs?.[0];
        let meeterDistance = legItem?.distance?.value;
        let walkingMiles =
          Math.round((meeterDistance * 0.000621371192 + Number.EPSILON) * 100) /
          100;
        // Prefer correctedDistanceMiles (min of walking + haversine) so staff
        // inside a building are not incorrectly blocked by routing overhead
        let calculatedMiles = result?.correctedDistanceMiles ?? walkingMiles;

        setMiles(calculatedMiles);
        setSelectedAddress(legItem?.end_address);
        // Compute max distance using the arguments passed in (not stale closure state).
        // On first client selection, selectedClient hasn't updated yet, so clientId param
        // is the only reliable source.
        let effectiveMaxDistance = 0.25;
        if (selectedType === "facility") {
          effectiveMaxDistance = facilityObj?.evvMaxDistance || 0.25;
        } else if (selectedType === "client") {
          const clientForDist = allPatients?.find((_p) => _p?.id === clientId);
          const clientFacilityIds = (clientForDist?.facilityId || []).map((f) =>
            typeof f === "string" ? f : f?.id || f?._id,
          );
          const evvFacility = facilities?.find(
            (f) =>
              clientFacilityIds.includes(f?.id || f?._id) &&
              f?.evvRequired === "yes",
          );
          effectiveMaxDistance = evvFacility?.evvMaxDistance || 0.25;
        }
        if (calculatedMiles > effectiveMaxDistance) {
          setMilesError(true);
        } else {
          setMilesError(false);
        }
        // Pass EVV location data up to parent
        setEvvLocationData?.({
          evvLocation: {
            lat: userLocation?.lat,
            lng: userLocation?.lng,
            accuracy: userLocation?.accuracy || null,
          },
          evvDistanceToSite: calculatedMiles,
          evvMatchedAddress: legItem?.end_address || null,
          locationSource: userLocation?.source || null,
          overrideAddress: overrideAddressParam || null,
          pickupLocation: selectedPickupObj || null,
        });
      } else {
        setSelectedAddress(destination);
        setMiles(null);
        // Still pass location data even when distance calc fails
        if (userLocation?.lat && userLocation?.lng) {
          setEvvLocationData?.({
            evvLocation: {
              lat: userLocation?.lat,
              lng: userLocation?.lng,
              accuracy: userLocation?.accuracy || null,
            },
            evvDistanceToSite: null,
            evvMatchedAddress: destination || null,
            locationSource: userLocation?.source || null,
            overrideAddress: overrideAddressParam || null,
            pickupLocation: selectedPickupObj || null,
          });
        }
      }
    } catch (err) {
      console.error(err);
      setIsClockLoading(false); // 🔥 stop loading
    } finally {
      setIsClockLoading(false); // 🔥 stop loading
    }
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={handleClose}
        size="lg"
        title="Clock In"
        centered
        radius={16}
        zIndex={1000}
        overlayProps={{
          backgroundOpacity: 0.55,
          blur: 3,
        }}
        className={classes.modal}
      >
        {/* <div className={classes.clockIconParent}>
          <img src={trackerIcon} alt="Tracker Icon" />
        </div>
        <p className={classes.warn}>Select Check In Type</p> */}
        {/* 
        <Select
          className={classes.facilityModal}
          label="Select Facility"
          size="md"
          data={profile?.user?.facilityId?.map((_f) => ({
            label: _f?.name,
            value: _f?.id,
          }))}
          placeholder="Select Facility"
          value={facilityValue}
          onChange={onChangeFacility}
          //disabled={view}
        /> */}

        <Flex align={"center"} justify="center" gap="md">
          <Box
            className={classes.noticeBox}
            style={{
              background: `${milesError ? "#FECDCA" : "#a6f4c580"}`,
              color: `${milesError ? "#B42318" : "#027A48"}`,
            }}
          >
            <Box
              className={classes.notice}
              style={{ color: `${milesError ? "#B42318" : "#027A48"}` }}
            >
              Notice
            </Box>
            {milesError ? (
              <Box className={classes.noticeText}>
                You can't Clock in until you are within{" "}
                <b>{currentMaxDistance}</b> Miles of the address
              </Box>
            ) : (
              <Box className={classes.noticeText}>You are set to Clock In</Box>
            )}
          </Box>
        </Flex>
        {checkInTypes.has("facility") || checkInTypes.has("client") ? (
          <Radio.Group
            name="check_in_type"
            label="Clock-in Type"
            required
            value={selectedType}
            onChange={radioChangeHandler}
            mt={10}
          >
            <Group mt="xs" className={classes.radioGroup}>
              {checkInTypes.has("facility") && (
                <Box
                  className={classes.radioBox}
                  style={{
                    cursor: "pointer",
                    border:
                      selectedType === "facility" ? "1px solid #FEB273" : "",
                    backgroundColor:
                      selectedType === "facility" ? "#feb2732b" : "",
                    width: boxWidth,
                  }}
                  onClick={() => setSelectedType("facility")}
                >
                  <Radio
                    value="facility"
                    label="Facility"
                    variant="outline"
                    size="sm"
                    color="orange"
                  />
                </Box>
              )}
              {checkInTypes.has("client") && (
                <Box
                  className={classes.radioBox}
                  style={{
                    cursor: "pointer",
                    border:
                      selectedType === "client" ? "1px solid #FEB273" : "",
                    backgroundColor:
                      selectedType === "client" ? "#feb2732b" : "",
                    width: boxWidth,
                  }}
                  onClick={() => setSelectedType("client")}
                >
                  <Radio
                    value="client"
                    label="Client"
                    variant="outline"
                    size="sm"
                    color="orange"
                  />
                </Box>
              )}
            </Group>
          </Radio.Group>
        ) : (
          <Text ff={"Inter"} fz={14} fw={500} color="red" my={20} ta={"center"}>
            No Facility clock-in type "Facility" or "Client" assigned
          </Text>
        )}

        {/* <Group mt="xs">
            {profile?.user?.facilityId?.find(
              (_f) => _f?.checkInType === "facility"
            ) && <Radio value="facility" label="Facility Type" />}
            {profile?.user?.facilityId?.find(
              (_f) => _f?.checkInType === "client"
            ) && <Radio value="client" label="Client Type" />}
          </Group> */}

        {selectedType === "facility" && (
          <Tooltip
            label={`${
              selectedType === "facility" || selectedType === "no-clockin"
                ? "Facility"
                : "Client"
            } chock-in type is not assigned to facility`}
            position="bottom"
            withArrow
            disabled={facilities?.length > 0}
          >
            <div>
              <Select
                mt={10}
                label="Select Facility"
                size="md"
                data={facilities?.map((_f) => ({
                  label: _f?.name,
                  value: _f?.id,
                }))}
                placeholder="Select Facility"
                value={facilityValue}
                rightSectionPointerEvents="none"
                rightSection={icon}
                onChange={onChangeFacility}

                //disabled={view}
              />
            </div>
          </Tooltip>
        )}

        {selectedType === "client" && (
          <Select
            label="Select Client"
            size="md"
            data={allPatients
              ?.filter(
                (_p) =>
                  _p?.deleted === false &&
                  _p?.active === true &&
                  // Exclude clients whose facilities are all "no-clockin"
                  _p?.facilityId?.some((fRef) => {
                    const fId =
                      typeof fRef === "string" ? fRef : fRef?.id || fRef?._id;
                    return facilities?.some((f) => (f?.id || f?._id) === fId);
                  }),
              )
              ?.map((_p) => ({
                label: _p?.name,
                value: _p?.id,
              }))}
            value={selectedClient}
            placeholder="Select Client"
            onChange={onChangeClient}
            rightSectionPointerEvents="none"
            rightSection={icon}
            mt={10}
            //disabled={view}
          />
        )}

        {selectedType === "client" && (
          <Select
            // className={classes.facilityModal}
            label="Select Service"
            size="md"
            data={allPatients
              ?.filter(
                (_p) =>
                  _p?.deleted === false &&
                  _p?.active === true &&
                  _p?.id === selectedClient,
              )
              ?.flatMap((_p) => {
                let i = _p?.form1?.services?.map((ser) => ({
                  label: ser?.servicetitle,
                  value: ser?.servicenumber,
                }));
                return i || [];
              })}
            value={selectedService}
            placeholder="Select Service"
            onChange={onChangeService}
            rightSectionPointerEvents="none"
            rightSection={icon}
            mt={10}
            disabled={!selectedClient}
          />
        )}

        {selectedType === "client" && selectedClient && !isLocationLoading && userLocation && !isUsingOneTimeAddress && (
          getClientAddressOptions(allPatients?.find((_p) => _p?.id === selectedClient)).length > 0 ? (
            <Select
              label="Select Address"
              placeholder="Select service location..."
              size="md"
              data={getClientAddressOptions(
                allPatients?.find((_p) => _p?.id === selectedClient)
              )}
              value={selectedPickupLocation}
              onChange={handlePickupLocationChange}
              rightSectionPointerEvents="none"
              rightSection={icon}
              mt={10}
            />
          ) : (
            <Text fz={13} color="red" mt={10}>
              No address found for this client. Please update the client profile.
            </Text>
          )
        )}

        <p className={classes.warn}>{warningText}</p>

         {/* Location status banner */}
        {isLocationLoading ? (
          <Flex align="center" gap={6} mt={8}>
            <Loader size="xs" color="gray" />
            <Text fz={12} color="dimmed">Detecting your location…</Text>
          </Flex>
        ) : !userLocation ? (
          <Flex
            mt={8}
            p={10}
            style={{
              background: "#FFF3CD",
              borderRadius: 8,
              border: "1px solid #FFC107",
            }}
            align="center"
            justify="space-between"
          >
            <Text fz={13} color="#856404">
              Please enable browser location to check in.
            </Text>
            <Button
              size="xs"
              variant="outline"
              color="yellow"
              style={{ borderColor: "#856404", color: "#856404" }}
              onClick={async () => {
                setIsLocationLoading(true);
                const loc = await getDeviceLocation();
                if (loc) {
                  setUserLocation(loc);
                  setLocationPermission("granted");
                } else {
                  setLocationPermission("denied");
                }
                setIsLocationLoading(false);
              }}
            >
              Retry
            </Button>
          </Flex>
        ) : null}

        {/* Facility type: same card design as client */}
        {selectedType === "facility" && checkInTypes.has("facility") && selectedFacility && (
          <Box
            style={{
              background: "#fff",
              border: "1px solid #e5e5ea",
              borderRadius: 8,
              margin: "1rem 0",
            }}
          >
            {/* Address — centered title + text */}
            <Box
              style={{
                padding: "16px 16px 12px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                gap: 4,
              }}
            >
              <Text fw={600} fz={16} color="#171717">Address</Text>
              {isClockLoading ? (
                <Skeleton height={14} width="65%" radius="sm" mt={4} />
              ) : userLocation && selectedAddress ? (
                <Text fz={16} color="#52525b" style={{ lineHeight: "24px" }}>
                  {selectedAddress}
                </Text>
              ) : null}
            </Box>

            {/* Divider */}
            <Box style={{ height: 1, background: "#e5e5e5", margin: "0 16px" }} />

            {/* Proximity indicator */}
            <Box
              style={{
                padding: "14px 16px 16px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 6,
              }}
            >
              {isLocationLoading || isClockLoading ? (
                <>
                  <Box
                    style={{
                      background: "#f3f4f6",
                      borderRadius: "50%",
                      width: 54,
                      height: 54,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Loader size="sm" color="gray" />
                  </Box>
                  <Text fz={14} fw={500} color="dimmed">Verifying your location…</Text>
                </>
              ) : (
                <>
                  <Box
                    style={{
                      background: milesError ? "#FEF3F2" : "#d9f2ef",
                      borderRadius: "50%",
                      width: 54,
                      height: 54,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: milesError ? "#b42318" : "#0f766e",
                    }}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width: 24, height: 24 }}>
                      <circle cx="12" cy="12" r="3"/>
                      <circle cx="12" cy="12" r="9"/>
                      <line x1="12" y1="1" x2="12" y2="4"/>
                      <line x1="12" y1="20" x2="12" y2="23"/>
                      <line x1="1" y1="12" x2="4" y2="12"/>
                      <line x1="20" y1="12" x2="23" y2="12"/>
                    </svg>
                  </Box>
                  <Flex align="center" gap={6}>
                    {!milesError && userLocation && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#027a48" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    )}
                    <Text fz={14} fw={500} color={milesError ? "#b42318" : "#262626"}>
                      {miles ? `${miles} Miles away` : userLocation ? "At Location" : "—"}
                    </Text>
                  </Flex>
                </>
              )}
            </Box>
          </Box>
        )}

        {/* Client type: mockup-style address card */}
        {selectedType === "client" && selectedClient && (
          showOneTimeEdit ? (
            /* Edit panel — replaces card while editing */
            <Box
              style={{
                background: "#fff",
                border: "1px solid #e5e5ea",
                borderRadius: 8,
                margin: "1rem 0",
                overflow: "visible",
              }}
            >
              {/* Header */}
              <Flex align="flex-start" justify="space-between" p={16} pb={8}>
                <Box>
                  <Text fw={600} fz={16} color="#171717">New address</Text>
                  <Text fz={13} color="#52525b" mt={2}>Use this for today's clock-in only.</Text>
                </Box>
                <Box
                  style={{
                    background: "#fff7ed",
                    border: "1px solid #fed7aa",
                    color: "#9a3412",
                    borderRadius: 16,
                    padding: "2px 10px",
                    fontSize: 12,
                    fontWeight: 600,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    flexShrink: 0,
                  }}
                >
                  One-time
                </Box>
              </Flex>

              {/* Disclaimer note */}
              <Box
                mx={16}
                mb={12}
                p={10}
                style={{
                  background: "#fffbeb",
                  border: "1px solid #fde68a",
                  borderRadius: 8,
                  display: "flex",
                  gap: 8,
                  alignItems: "flex-start",
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#92400e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <Text fz={13} style={{ color: "#92400e", lineHeight: "18px" }}>
                  This address won't be saved to the client's record. It will only be used to verify your location for this clock-in.
                </Text>
              </Box>

              {/* Street address with Google autocomplete */}
              <Box px={16} mb={12}>
                <Box style={{ position: "relative" }}>
                  <TextInput
                    label="Street address"
                    placeholder="e.g. 1400 Washington Street"
                    size="sm"
                    value={oneTimeForm.address}
                    onChange={(e) => handleOneTimeAddressSearch(e.target.value)}
                    onBlur={() => setTimeout(() => setShowOneTimeSuggestions(false), 200)}
                    onFocus={() => oneTimeSuggestions.length > 0 && setShowOneTimeSuggestions(true)}
                    rightSection={oneTimeAddrLoading ? <Loader size="xs" /> : null}
                  />
                  {showOneTimeSuggestions && oneTimeSuggestions.length > 0 && (
                    <Box
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        zIndex: 400,
                        background: "#fff",
                        border: "1px solid #dee2e6",
                        borderRadius: 8,
                        maxHeight: 180,
                        overflowY: "auto",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      }}
                    >
                      {oneTimeSuggestions.map((s, i) => (
                        <Box
                          key={s.place_id || i}
                          onMouseDown={() => handleOneTimeSelectAddress(s)}
                          style={{ padding: "8px 12px", cursor: "pointer", fontSize: 13 }}
                          sx={{ "&:hover": { background: "#f8f9fa" } }}
                        >
                          {s.formatted_address}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
                <Flex gap={10} mt={10}>
                  <TextInput
                    label="City"
                    placeholder="City"
                    size="sm"
                    style={{ flex: 1 }}
                    value={oneTimeForm.city}
                    onChange={(e) => setOneTimeForm((prev) => ({ ...prev, city: e.target.value }))}
                  />
                  <TextInput
                    label="State"
                    placeholder="AZ"
                    size="sm"
                    style={{ flex: 1 }}
                    value={oneTimeForm.state}
                    onChange={(e) => setOneTimeForm((prev) => ({ ...prev, state: e.target.value }))}
                  />
                  <TextInput
                    label="ZIP"
                    placeholder="85007"
                    size="sm"
                    style={{ width: 100 }}
                    value={oneTimeForm.zipCode}
                    onChange={(e) => setOneTimeForm((prev) => ({ ...prev, zipCode: e.target.value }))}
                  />
                </Flex>
              </Box>

              {/* Edit footer */}
              <Flex
                justify="space-between"
                align="center"
                style={{ borderTop: "1px solid #e5e5e5", padding: "10px 16px", background: "#fafafa" }}
              >
                <UnstyledButton
                  onClick={() => setShowOneTimeEdit(false)}
                  style={{ color: "#f97316", fontSize: 14, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 6px", borderRadius: 6 }}
                >
                  ← Use saved address
                </UnstyledButton>
                <Button
                  size="sm"
                  disabled={!oneTimeForm.address.trim()}
                  onClick={handleUseOneTimeAddress}
                  style={{ background: "#ffaf4a", color: "#000", border: "none" }}
                >
                  Use this address
                </Button>
              </Flex>
            </Box>
          ) : (
            /* Saved / address view — matches mockup card layout */
            <Box
              style={{
                background: "#fff",
                border: "1px solid #e5e5ea",
                borderRadius: 8,
                margin: "1rem 0",
              }}
            >
              {/* Address — centered title + text */}
              <Box
                style={{
                  padding: "16px 16px 12px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  gap: 4,
                }}
              >
                <Text fw={600} fz={16} color="#171717">
                  {isUsingOneTimeAddress ? "Address (one-time)" : "Address"}
                </Text>
                {isClockLoading ? (
                  <Skeleton height={14} width="65%" radius="sm" mt={4} />
                ) : (selectedAddress || buildClientAddress(allPatients?.find((_p) => _p?.id === selectedClient))) ? (
                  <Text fz={16} color="#52525b" style={{ lineHeight: "24px" }}>
                    {selectedAddress || buildClientAddress(allPatients?.find((_p) => _p?.id === selectedClient))}
                  </Text>
                ) : null}
              </Box>

              {/* Divider */}
              <Box style={{ height: 1, background: "#e5e5e5", margin: "0 16px" }} />

              {/* Proximity indicator */}
              {(() => {
                const clientObj = allPatients?.find((_p) => _p?.id === selectedClient);
                const hasNoAddress = !isUsingOneTimeAddress && !buildClientAddress(clientObj) && !selectedAddress;
                return (
                  <Box
                    style={{
                      padding: "14px 16px 16px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    {isLocationLoading || isClockLoading ? (
                      /* Verifying state */
                      <>
                        <Box
                          style={{
                            background: "#f3f4f6",
                            borderRadius: "50%",
                            width: 54,
                            height: 54,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Loader size="sm" color="gray" />
                        </Box>
                        <Text fz={14} fw={500} color="dimmed">Verifying your location…</Text>
                      </>
                    ) : hasNoAddress ? (
                      /* No address on file — neutral state */
                      <>
                        <Box
                          style={{
                            background: "#f3f4f6",
                            borderRadius: "50%",
                            width: 54,
                            height: 54,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#9ca3af",
                          }}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width: 24, height: 24 }}>
                            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
                            <line x1="4" y1="4" x2="20" y2="20"/>
                          </svg>
                        </Box>
                        <Text fz={14} fw={500} color="dimmed">No address on file</Text>
                      </>
                    ) : (
                      /* At-location / distance state */
                      <>
                        <Box
                          style={{
                            background: milesError ? "#FEF3F2" : "#d9f2ef",
                            borderRadius: "50%",
                            width: 54,
                            height: 54,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: milesError ? "#b42318" : "#0f766e",
                          }}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width: 24, height: 24 }}>
                            <circle cx="12" cy="12" r="3"/>
                            <circle cx="12" cy="12" r="9"/>
                            <line x1="12" y1="1" x2="12" y2="4"/>
                            <line x1="12" y1="20" x2="12" y2="23"/>
                            <line x1="1" y1="12" x2="4" y2="12"/>
                            <line x1="20" y1="12" x2="23" y2="12"/>
                          </svg>
                        </Box>
                        <Flex align="center" gap={6}>
                          {!milesError && userLocation && (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#027a48" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12"/>
                            </svg>
                          )}
                          <Text fz={14} fw={500} color={milesError ? "#b42318" : "#262626"}>
                            {miles ? `${miles} Miles away` : userLocation ? "At Location" : "—"}
                          </Text>
                        </Flex>
                      </>
                    )}
                  </Box>
                );
              })()}

              {/* Action row — dashed divider + "Client at a different location?" */}
              <Flex
                align="center"
                justify="center"
                gap={6}
                style={{
                  borderTop: "1px dashed #e5e5e5",
                  padding: "10px 16px",
                  background: "#fafafa",
                }}
              >
                {!isUsingOneTimeAddress ? (
                  <>
                    <Text fz={14} color="#52525b">Client at a different location?</Text>
                    <UnstyledButton
                      onClick={() => setShowOneTimeEdit(true)}
                      style={{ color: "#f97316", fontSize: 14, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 6px", borderRadius: 6 }}
                    >
                      + Add new address
                    </UnstyledButton>
                  </>
                ) : (
                  <>
                    <Text fz={14} color="#52525b">Using a one-time address.</Text>
                    <UnstyledButton
                      onClick={handleRevertToSavedAddress}
                      style={{ color: "#f97316", fontSize: 14, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 6px", borderRadius: 6 }}
                    >
                      ← Use client's saved address
                    </UnstyledButton>
                  </>
                )}
              </Flex>
            </Box>
          )
        )}

       

        <Flex justify="flex-end">
          <Group mt="xl">
            <Button onClick={handleClose} className={classes.cancelBtn}>
              Cancel
            </Button>
            <Button
              className={classes.action}
              onClick={clockInHandler}
              loading={isClockLoading || isSubmitting}
              disabled={
                isSubmitting
                  ? true
                  : isLocationLoading
                    ? true
                    : !userLocation
                      ? true
                      : selectedType === "client" && !selectedClient
                        ? true
                        : selectedType === "client" && selectedClient &&
                          !isUsingOneTimeAddress &&
                          getClientAddressOptions(allPatients?.find((_p) => _p?.id === selectedClient)).length === 0
                          ? true
                          : selectedType !== "client" && !selectedFacility
                            ? true
                            : selectedFacility && warningText
                              ? true
                              : milesError
                                ? true
                                : false
              }
            >
              Clock-in
            </Button>
          </Group>
        </Flex>
      </Modal>
    </>
  );
}

export default SelectCheckInType;

